import React, { useState, useEffect } from 'react';
import {
  Tabs,
  Tab,
  TabPanel,
  TabPanels,
  Box,
  useColorModeValue,
  Heading,
  Divider,
  Icon,
  VStack,
  Breadcrumb,
  BreadcrumbItem,
  Text,
  Flex,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import { LuLogs } from 'react-icons/lu';
import {
  FaChevronLeft,
  FaChevronRight,
  FaEthernet,
  FaSlidersH,
  FaCodeBranch,
  FaChartLine,
  FaTachometerAlt,
  FaPlusCircle,
  // FaUsers,
} from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';

import Interface from '../interface';
import Configure from '../configure';
import Logic from '../logic';
import TelemetryMonitor from '../monitor';
import Logs from '../logs';
import Thresholds from '../thresholds';
import Logic2 from '../logic/Logic2';
import AddConfiguration from '../addConfiguration/AddConfiguration';

const EditDevice = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // initialize tabIndex from navigation state if provided (activeTab)
  const [tabIndex, setTabIndex] = useState(location.state?.activeTab ?? 0);
  const [collapsed, setCollapsed] = useState(false);

  // If a navigation provided activeTab, react to it and then clear the state so it doesn't persist
  useEffect(() => {
    if (location?.state?.activeTab !== undefined) {
      setTabIndex(location.state.activeTab);
      // replace history entry to clear the state to avoid re-trigger on refresh/back
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location?.state, navigate, location?.pathname]);

  // Scroll to top on mount and tab change
  useEffect(() => window.scrollTo(0, 0), []);
  useEffect(() => window.scrollTo(0, 0), [tabIndex]);

  // Theme colors
  // const bg = useColorModeValue('white', 'gray.900');
  const breadCrumBg = useColorModeValue('gray.200', 'navy.700');
  // const tabListBg = useColorModeValue('white', 'navy.700');
  const activeTabBg = useColorModeValue('navy.50', 'navy.600');
  const iconBg = useColorModeValue('gray.50', 'navy.900');
  const sideBarBg = useColorModeValue('white', 'navy.700');
  const activeTabColor = useColorModeValue('gray.700', 'white');

  // Sidebar widths
  const sidebarExpandedWidth = '240px';
  const sidebarCollapsedWidth = '70px';
  const sidebarWidth = collapsed ? sidebarCollapsedWidth : sidebarExpandedWidth;
  const topOffset = '70px';

  // Tabs
  const tabItems = [
    { label: 'Interfaces', icon: FaEthernet, color: 'teal.400' },
    { label: 'Slaves', icon: FaSlidersH, color: 'orange.400' },
    { label: 'Add Configuration', icon: FaPlusCircle, color: 'orange.400' },

    { label: 'Logic', icon: FaCodeBranch, color: 'purple.400' },
    { label: 'Logic2', icon: FaCodeBranch, color: 'purple.400' },

    { label: 'Monitor', icon: FaChartLine, color: 'blue.400' },
    { label: 'Logs', icon: LuLogs, color: 'red' },
    { label: 'Thresholds ', icon: FaTachometerAlt, color: 'pink.400' },
    // { label: 'Groups ', icon: FaUsers, color: 'green.400' },
  ];

  // Breadcrumb
  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'Edit Device', path: null },
  ];

  // Map actual tab index to display tab index for sidebar selection
  const getDisplayTabIndex = (actualIndex) => {
    // When on Add Configuration tab (index 2), show Slaves tab (index 1) as selected
    if (actualIndex === 2) return 1;
    // When on Logic2 tab (index 4), show Logic tab (index 3) as selected
    if (actualIndex === 4) return 3;
    return actualIndex;
  };

  const displayTabIndex = getDisplayTabIndex(tabIndex);

  return (
    <Box minH="100vh" p={4}>
      {/* Breadcrumb */}
      <Breadcrumb
        mb={6}
        fontWeight="bold"
        fontSize="sm"
        color={activeTabColor}
        backgroundColor={breadCrumBg}
        padding={2}
        borderRadius="md"
        width="fit-content"
        separator=">"
        ml={sidebarWidth}
        transition="margin-left 0.3s ease"
      >
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;
          return (
            <BreadcrumbItem key={index} isCurrentPage={isLast}>
              {isLast ? (
                <Text color="gray.400" fontWeight="bold">
                  {item.label}
                </Text>
              ) : (
                <Text
                  cursor="pointer"
                  _hover={{ textDecoration: 'underline' }}
                  onClick={() => navigate(item.path)}
                >
                  {item.label}
                </Text>
              )}
            </BreadcrumbItem>
          );
        })}
      </Breadcrumb>

      <Tabs
        index={tabIndex}
        onChange={(index) => setTabIndex(index)}
        orientation="vertical"
        variant="unstyled"
        isLazy
      >
        {/* Sidebar */}
        <Card
          position="fixed"
          top={topOffset}
          padding={collapsed ? 1 : 3}
          left={0}
          width={sidebarWidth}
          height={`calc(100vh - ${topOffset})`}
          borderRadius="none"
          zIndex={10}
          bg={sideBarBg}
          transition="width 0.3s ease"
        >
          {/* Header with collapse toggle */}
          <Flex align="center" justify="space-between" mb={4} px={4} pt={2}>
            {!collapsed && <Heading size="md">Configure</Heading>}

            <Icon
              mt={collapsed ? 3 : 0}
              mx={collapsed ? 2 : 0}
              as={collapsed ? FaChevronRight : FaChevronLeft}
              onClick={() => setCollapsed(!collapsed)}
              cursor="pointer"
              boxSize={4}
            />
          </Flex>

          <Divider mb={4} />

          {/* Sidebar Tabs */}
          <VStack spacing={2} align="stretch" px={collapsed ? 1 : 2}>
            {/*
              Render all tab placeholders in-order. The 'Add Configuration' tab
              is rendered but hidden so TabPanels keep index alignment while
              no visible sidebar tab is selected when its panel is active.
            */}
            {tabItems.map((tab, i) => (
              <Tab
                key={i}
                // hide the Add Configuration and Logic2 tabs visually but keep them in the DOM/order
                style={
                  tab.label === 'Add Configuration' || tab.label === 'Logic2'
                    ? { display: 'none' }
                    : {}
                }
                aria-hidden={
                  tab.label === 'Add Configuration' || tab.label === 'Logic2'
                    ? 'true'
                    : 'false'
                }
                _selected={{ bg: activeTabBg, color: activeTabColor }}
                bg={
                  collapsed ? iconBg : displayTabIndex === i ? activeTabBg : ''
                }
                color={displayTabIndex === i ? activeTabColor : ''}
                borderRadius="md"
                py={3}
                px={2}
                justifyContent={collapsed ? 'center' : 'flex-start'}
                textAlign="left"
                display="flex"
                alignItems="center"
                gap={collapsed ? 0 : 3}
                fontWeight="medium"
                fontSize="md"
                cursor="pointer"
              >
                <Icon
                  as={tab.icon}
                  boxSize={5}
                  padding={0.2}
                  color={tab.color}
                />
                {!collapsed &&
                  tab.label !== 'Add Configuration' &&
                  tab.label !== 'Logic2' &&
                  tab.label}
              </Tab>
            ))}
          </VStack>
        </Card>

        {/* Tab Content */}
        <Box
          ml={sidebarWidth}
          width={`calc(100% - ${sidebarWidth})`}
          minHeight="100vh"
          transition="margin-left 0.3s ease, width 0.3s ease"
        >
          <TabPanels height="100%">
            <TabPanel p={0} height="100%">
              <Interface />
            </TabPanel>
            <TabPanel p={0} height="100%">
              <Configure />
            </TabPanel>
            <TabPanel p={0} height="100%">
              <AddConfiguration />
            </TabPanel>
            <TabPanel p={0} height="100%">
              <Logic />
            </TabPanel>
            <TabPanel p={0} height="100%">
              <Logic2 />
            </TabPanel>

            <TabPanel p={0} height="100%">
              <TelemetryMonitor />
            </TabPanel>
            <TabPanel p={0} height="100%">
              <Logs />
            </TabPanel>
            <TabPanel p={0} height="100%">
              <Thresholds />
            </TabPanel>
            {/* <TabPanel p={0} height="100%">
              <Group />
            </TabPanel> */}
          </TabPanels>
        </Box>
      </Tabs>
    </Box>
  );
};

export default EditDevice;
